import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { MDSSkeleton, MDSText, MDSTextColor, MDSTextVariant } from "@/mds";
import { AgentSection, AgentSectionCard } from "@/pages/agent/components/agent-section";
import { AgentTaskListItem } from "@/pages/agent/tasks/agent-task-list-item";
import type { TaskRowViewModel } from "@/pages/agent/tasks/agent-tasks-page.utils";

export const AgentRelatedTasksCard = ({
  dataLoaded,
  emptyLabel,
  showProject = true,
  taskRows,
  title,
  trailing,
  onOpenActivities,
  onOpenNote,
  onOpenProject,
  onOpenTask,
  onToggleTaskComplete,
}: {
  dataLoaded: boolean;
  emptyLabel: string;
  showProject?: boolean;
  taskRows: TaskRowViewModel[];
  title: string;
  trailing?: ReactNode;
  onOpenActivities: ({ taskId }: { taskId: string }) => void;
  onOpenNote: ({ noteId }: { noteId: string }) => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTask: ({ taskId }: { taskId: string }) => void;
  onToggleTaskComplete: ({ taskId }: { taskId: string }) => void;
}) => (
  <AgentSection
    title={title}
    trailing={
      dataLoaded || trailing ? (
        <>
          {dataLoaded ? (
            <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
              {taskRows.length} {taskRows.length === 1 ? "task" : "tasks"}
            </MDSText>
          ) : null}
          {trailing}
        </>
      ) : undefined
    }
  >
    <AgentSectionCard>
      {!dataLoaded && taskRows.length === 0 ? (
        <StyledLoadingState aria-label={`Loading ${title.toLowerCase()}`}>
          <MDSSkeleton height={18} width="65%" />
          <MDSSkeleton height={14} width="45%" />
        </StyledLoadingState>
      ) : taskRows.length === 0 ? (
        <StyledEmptyState color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
          {emptyLabel}
        </StyledEmptyState>
      ) : (
        <StyledTaskList aria-label={title} role="list">
          {taskRows.map(taskRow => (
            <AgentTaskListItem
              key={taskRow.task.id}
              showProject={showProject}
              taskRow={taskRow}
              onOpenActivities={onOpenActivities}
              onOpenNote={onOpenNote}
              onOpenProject={onOpenProject}
              onOpenTask={onOpenTask}
              onToggleTaskComplete={onToggleTaskComplete}
            />
          ))}
        </StyledTaskList>
      )}
    </AgentSectionCard>
  </AgentSection>
);

const StyledTaskList = styled.div`
  max-height: 380px;
  overflow-y: auto;
`;

const StyledEmptyState = styled(MDSText)`
  padding: ${({ theme }) => theme.spacing[5]};
`;

const StyledLoadingState = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[5]};
`;
